// 1. Imports React
import { useEffect, useMemo, useState } from "react";
// 2. Imports externes
import { useDebounce } from "use-debounce";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Clock, Plus } from "lucide-react";

// 3. Imports composants UI
import { Button } from "./ui/button";
import { ConfirmDeleteDialog } from "./ConfirmDeleteDialog";
import { PaginationControls } from "./ui/pagination-controls";
import WorSessionFormDialog from "./forms/WorSessionFormDialog";

// 4. Imports hooks personnalisés
import { useAuth } from "@/contexts/AuthContext";
import { useCompanies } from "@/hooks/useCompanies";
import {
  useDeleteWorkSession,
  useFetchWorkSessions,
} from "@/hooks/useWorkSessions";

// 5. Imports types
import type { WorkSession } from "@/types/workSession.type";

// 6. Imports utils
import {
  formatDuration,
  getStartOfDayUTC,
  getEndOfDayUTC,
} from "@/lib/date.utils";
import { AllSessionsFiltersBar } from "./sessions/AllSessionsFiltersBar";
import { AllSessionsGroupedList } from "./sessions/AllSessionsGroupedList";

// 7. Interface
interface AllSessionsProps {
  companyId?: string | null;
}

// 8. Composant
const AllSessions = ({ companyId }: AllSessionsProps) => {
  // ========== A. Hooks React ==========
  const [filterType, setFilterType] = useState<
    "all" | "day" | "month" | "period"
  >("all");
  const [selectedDay, setSelectedDay] = useState<Date>();
  const [selectedMonth, setSelectedMonth] = useState<Date>(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [periodStart, setPeriodStart] = useState<Date>();
  const [periodEnd, setPeriodEnd] = useState<Date>();
  const [dayPopoverOpen, setDayPopoverOpen] = useState(false);
  const [monthPopoverOpen, setMonthPopoverOpen] = useState(false);
  const [periodStartPopoverOpen, setPeriodStartPopoverOpen] = useState(false);
  const [periodEndPopoverOpen, setPeriodEndPopoverOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [searchQuery, setSearchQuery] = useState("");
  const [companyFilter, setCompanyFilter] = useState<string>("all");
  const [companyPopoverOpen, setCompanyPopoverOpen] = useState(false);
  const [companySearch, setCompanySearch] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(
    null
  );
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [editingSession, setEditingSession] = useState<WorkSession | null>(
    null
  );
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [workSessionToDelete, setWorkSessionToDelete] =
    useState<WorkSession | null>(null);
  const [showActiveSessions, setShowActiveSessions] = useState<boolean>(false);
  // État pour gérer quelles dates sont ouvertes/fermées
  const [openDates, setOpenDates] = useState<Set<string>>(new Set()); //
  // Debounce pour les recherches
  const [debouncedSearchQuery] = useDebounce(searchQuery, 500);
  const [debouncedCompanySearch] = useDebounce(companySearch, 500);

  // ========== B. Hooks personnalisés ==========
  const { user } = useAuth();
  const isSuperuser = user?.role === "SUPERUSER";

  // Récupérer les entreprises uniquement pour SUPERUSER avec recherche
  const { data: companiesData, isFetching: isFetchingCompanies } = useCompanies(
    {
      search: debouncedCompanySearch || undefined,
      limit: 100,
    },
    { enabled: isSuperuser }
  );

  // Mutations pour les sessions
  const { mutate: deleteSessionMutation, isPending: isDeleting } =
    useDeleteWorkSession();

  // Réinitialiser la page quand les filtres changent
  useEffect(() => {
    setCurrentPage(1);
  }, [
    filterType,
    selectedDay,
    selectedMonth,
    periodStart,
    periodEnd,
    debouncedSearchQuery,
    companyFilter,
  ]);

  // ========== C. Valeurs calculées / Helpers ==========
  // Les fonctions de gestion de dates sont maintenant importées depuis @/lib/date.utils

  // Fonction pour calculer la plage de dates selon le filtre
  const getDateRange = () => {
    if (filterType === "all") {
      const now = new Date();
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return {
        startDate: getStartOfDayUTC(firstDayOfMonth),
        endDate: getEndOfDayUTC(lastDayOfMonth),
      };
    }

    if (filterType === "day") {
      if (selectedDay) {
        const startOfDay = getStartOfDayUTC(selectedDay);
        const endOfDay = getEndOfDayUTC(selectedDay);
        return {
          startDate: startOfDay,
          endDate: endOfDay,
        };
      }
      const now = new Date();
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return {
        startDate: getStartOfDayUTC(firstDayOfMonth),
        endDate: getEndOfDayUTC(lastDayOfMonth),
      };
    }

    if (filterType === "month") {
      if (selectedMonth) {
        const firstDayOfMonth = new Date(
          selectedMonth.getFullYear(),
          selectedMonth.getMonth(),
          1
        );
        const lastDayOfMonth = new Date(
          selectedMonth.getFullYear(),
          selectedMonth.getMonth() + 1,
          0
        );
        return {
          startDate: getStartOfDayUTC(firstDayOfMonth),
          endDate: getEndOfDayUTC(lastDayOfMonth),
        };
      }
      const now = new Date();
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return {
        startDate: getStartOfDayUTC(firstDayOfMonth),
        endDate: getEndOfDayUTC(lastDayOfMonth),
      };
    }

    if (filterType === "period") {
      if (periodStart && periodEnd) {
        return {
          startDate: getStartOfDayUTC(periodStart),
          endDate: getEndOfDayUTC(periodEnd),
        };
      }
      const now = new Date();
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return {
        startDate: getStartOfDayUTC(firstDayOfMonth),
        endDate: getEndOfDayUTC(lastDayOfMonth),
      };
    }

    // Par défaut : retourner le mois actuel
    const now = new Date();
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      startDate: getStartOfDayUTC(firstDayOfMonth),
      endDate: getEndOfDayUTC(lastDayOfMonth),
    };
  };

  const dateRange = getDateRange();

  // Récupérer les sessions de travail
  const { data: workSessionsData, isLoading: workSessionsLoading } =
    useFetchWorkSessions({
      companyId:
        companyFilter === "all" ? companyId || undefined : companyFilter,
      ...dateRange,
      page: currentPage,
      limit: itemsPerPage,
      groupBy: "date,employee",
      search: debouncedSearchQuery.trim() || undefined,
      active: showActiveSessions || undefined,
    });

  // Valeurs calculées
  const companies = companiesData?.companies || [];
  const pagination = workSessionsData?.pagination || null;
  const stats = workSessionsData?.stats || null;

  // Helper pour parser une date YYYY-MM-DD en date locale (pas UTC)
  // parseISO interprète "YYYY-MM-DD" comme UTC, ce qui cause un décalage d'un jour
  const parseLocalDate = (dateString: string): Date => {
    // Format: "YYYY-MM-DD" -> créer une date locale à minuit
    const [year, month, day] = dateString.split("-").map(Number);
    return new Date(year, month - 1, day); // month - 1 car les mois sont 0-indexés
  };

  // Grouper les sessions par date puis par employé
  const groupedSessions = useMemo(() => {
    if (workSessionsData?.grouped) {
      return Object.entries(workSessionsData.grouped)
        .sort(([dateA], [dateB]) => dateB.localeCompare(dateA))
        .map(([date, employees]) => {
          const employeeArray = Object.entries(employees).sort(
            ([nameA], [nameB]) => nameA.localeCompare(nameB)
          );

          return {
            date,
            dateFormatted: format(parseLocalDate(date), "EEEE dd MMMM yyyy", {
              locale: fr,
            }),
            employees: employeeArray,
          };
        });
    }
  }, [workSessionsData?.grouped]);


  // Fonction pour obtenir le nom de l'entreprise sélectionnée
  const getSelectedCompanyName = () => {
    if (companyFilter === "all") return "Toutes les entreprises";
    const company = companies.find((c) => c.id === companyFilter);
    return company?.name || "Toutes les entreprises";
  };

  // Fonction pour réinitialiser les filtres
  const resetFilters = () => {
    setFilterType("all");
    setSelectedDay(undefined);
    setSelectedMonth(
      new Date(new Date().getFullYear(), new Date().getMonth(), 1)
    );
    setPeriodStart(undefined);
    setPeriodEnd(undefined);
    setSearchQuery("");
    setCompanyFilter("all");
    setShowActiveSessions(false);
  };

  // Vérifier s'il y a des filtres actifs
  const hasActiveFilters =
    filterType !== "all" ||
    selectedDay !== undefined ||
    (selectedMonth &&
      (selectedMonth.getMonth() !== new Date().getMonth() ||
        selectedMonth.getFullYear() !== new Date().getFullYear())) ||
    periodStart !== undefined ||
    periodEnd !== undefined ||
    searchQuery.trim() !== "" ||
    (isSuperuser && companyFilter !== "all") ||
    showActiveSessions;

  // Fonction pour ouvrir/fermer une date
  const toggleDate = (date: string) => {
    setOpenDates((prev) => {
      const next = new Set(prev); // Créer un nouvel ensemble avec la date
      if (next.has(date)) {
        next.delete(date);
      } else {
        next.add(date);
      }
      return next;
    });
  };

  // ========== D. Handlers ==========
  const handleAddSession = (employeeId?: string, date?: Date) => {
    setEditingSession(null);
    setIsDialogOpen(true);
    setSelectedEmployeeId(employeeId || null);
    setSelectedDate(date || null);
  };

  const handleEdit = (session: WorkSession) => {
    setIsDialogOpen(false);
    setEditingSession(session);
  };

  const handleCloseSessionFormDialog = () => {
    setIsDialogOpen(false);
    setEditingSession(null);
    setSelectedEmployeeId(null);
    setSelectedDate(null);
  };

  const handleDeleteClick = (session: WorkSession) => {
    setIsDeleteDialogOpen(true);
    setWorkSessionToDelete(session);
  };

  const handleConfirmDelete = () => {
    if (workSessionToDelete) {
      deleteSessionMutation(workSessionToDelete.id, {
        onSuccess: () => {
          setIsDeleteDialogOpen(false);
          setWorkSessionToDelete(null);
        },
      });
    }
  };

  // ========== E. JSX ==========
  return (
    <div className="space-y-6">
      {/* En-tête avec bouton Ajouter */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3">
            <Clock className="h-8 w-8 text-primary" />
            Gestion des Sessions
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            Suivez les sessions de travail des employés
          </p>
        </div>
        <Button
          onClick={() => handleAddSession()}
          className="gap-2 w-full sm:w-auto"
          size="sm"
        >
          <Plus className="h-4 w-4" />
          <span className="hidden sm:inline">Ajouter une session</span>
          <span className="sm:hidden">Ajouter</span>
        </Button>
      </div>

      {/* Card avec filtres et tableau */}
      <div className="rounded-lg bg-card p-4 sm:p-6">
        {/* Filtres */}
        <AllSessionsFiltersBar
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          filterType={filterType}
          onFilterTypeChange={setFilterType}
          selectedDay={selectedDay}
          onSelectedDayChange={setSelectedDay}
          dayPopoverOpen={dayPopoverOpen}
          onDayPopoverOpenChange={setDayPopoverOpen}
          selectedMonth={selectedMonth}
          onSelectedMonthChange={(month) => {
            if (month) setSelectedMonth(month);
          }}
          monthPopoverOpen={monthPopoverOpen}
          onMonthPopoverOpenChange={setMonthPopoverOpen}
          periodStart={periodStart}
          periodEnd={periodEnd}
          onPeriodStartChange={setPeriodStart}
          onPeriodEndChange={setPeriodEnd}
          periodStartPopoverOpen={periodStartPopoverOpen}
          onPeriodStartPopoverOpenChange={setPeriodStartPopoverOpen}
          periodEndPopoverOpen={periodEndPopoverOpen}
          onPeriodEndPopoverOpenChange={setPeriodEndPopoverOpen}
          isSuperuser={isSuperuser}
          companies={companies}
          isFetchingCompanies={isFetchingCompanies}
          companyFilter={companyFilter}
          onCompanyFilterChange={setCompanyFilter}
          companyPopoverOpen={companyPopoverOpen}
          onCompanyPopoverOpenChange={setCompanyPopoverOpen}
          companySearch={companySearch}
          onCompanySearchChange={setCompanySearch}
          selectedCompanyLabel={getSelectedCompanyName()}
          showActiveSessions={showActiveSessions}
          onShowActiveSessionsChange={setShowActiveSessions}
          hasActiveFilters={hasActiveFilters}
          onResetFilters={resetFilters}
        />

        {/* Sessions groupées par date puis par employé */}
        <AllSessionsGroupedList
          loading={workSessionsLoading}
          groupedSessions={groupedSessions}
          openDates={openDates}
          onToggleDate={toggleDate}
          isSuperuser={isSuperuser}
          onAddSession={handleAddSession}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
        />

        {/* Footer avec stats */}
        <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border-t pt-4">
          <p className="text-xs sm:text-sm text-muted-foreground">
            {pagination?.total || 0} session(s) • Total des heures :{" "}
            <span className="font-semibold text-foreground">
              {formatDuration(stats?.totalHours || 0)}
            </span>
          </p>
        </div>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="mt-4">
            <PaginationControls
              page={pagination.page}
              totalPages={pagination.totalPages}
              totalItems={pagination.total}
              itemsPerPage={pagination.limit}
              onPageChange={(newPage) => {
                setCurrentPage(newPage);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              onPageSizeChange={(newSize) => {
                setItemsPerPage(newSize);
                setCurrentPage(1);
              }}
            />
          </div>
        )}
      </div>

      {/* Création / modification de session (même formulaire) */}
      <WorSessionFormDialog
        open={isDialogOpen || !!editingSession}
        onOpenChange={(open) => {
          if (!open) handleCloseSessionFormDialog();
        }}
        employeeId={editingSession?.employeeId ?? selectedEmployeeId ?? ""}
        sessionToEdit={editingSession}
        onSuccess={handleCloseSessionFormDialog}
        date={editingSession ? undefined : selectedDate || undefined}
      />

      {/* Dialog pour confirmer la suppression */}
      <ConfirmDeleteDialog
        title="Supprimer la session de travail"
        description="Êtes-vous sûr de vouloir supprimer cette session de travail ? Cette action est irréversible."
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
        sessionDate={
          workSessionToDelete
            ? format(new Date(workSessionToDelete.startTime), "PPP", {
                locale: fr,
              })
            : undefined
        }
        employeeName={
          workSessionToDelete?.user?.firstName
            ? `${workSessionToDelete?.user?.firstName} ${workSessionToDelete?.user?.lastName}`
            : ""
        }
        sessionDuration={formatDuration(workSessionToDelete?.duration || 0)}
      />
    </div>
  );
};

export default AllSessions;
